<script setup lang="ts">
  // Manual deposit/withdrawal modal (cash only), mirroring the server-side
  // insufficiency guard for outflows so the user never hits a 400 blindly.
  // Opened via showDynamicDialog<IPosV2CajaManualMovementResult | null>(
  //   ..., 'caja-manual-movement').
  import { computed, getCurrentInstance, ref } from 'vue';
  import { roundPrice } from '@/utils';
  import { useModal } from '@/composition/use-modal';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';

  export interface IPosV2CajaManualMovementResult {
    amount: number;
    description: string | null;
  }

  const MAX_COMMENT_CHARS = 140;

  const props = withDefaults(
    defineProps<{
      type: 'inflow' | 'outflow';
      /** Current cash for outflow validation; null skips the local guard. */
      availableCash?: number | null;
      currencySymbol?: string;
    }>(),
    {
      availableCash: null,
      currencySymbol: '$',
    }
  );

  const modals = useModal();

  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const isInflow = computed(() => props.type === 'inflow');

  const title = computed(() =>
    isInflow.value ? i18nT('cajaV2.deposit') : i18nT('cajaV2.withdraw')
  );

  const message = computed(() =>
    isInflow.value
      ? i18nT('cajaV2.manualMovementDepositMessage')
      : i18nT('cajaV2.manualMovementWithdrawMessage')
  );

  const amountValue = ref('');
  const comment = ref('');

  const parsedAmount = computed<number | null>(() => {
    const raw = amountValue.value.trim().replace(',', '.');
    if (!raw) return null;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed <= 0) return null;
    return roundPrice(parsed);
  });

  /** Mirrors the server guard: 'No hay suficiente efectivo en la caja'. */
  const exceedsAvailableCash = computed(() => {
    if (isInflow.value) return false;
    if (props.availableCash == null) return false;
    if (parsedAmount.value == null) return false;
    return parsedAmount.value > roundPrice(props.availableCash);
  });

  const amountError = computed<string | null>(() => {
    if (amountValue.value.trim() === '') return null;
    if (parsedAmount.value == null) return i18nT('cajaV2.amountInvalid');
    if (exceedsAvailableCash.value)
      return i18nT('cajaV2.insufficientCash');
    return null;
  });

  const commentLength = computed(() => comment.value.length);

  const commentTooLong = computed(
    () => commentLength.value > MAX_COMMENT_CHARS
  );

  const canSubmit = computed(
    () =>
      parsedAmount.value != null &&
      !exceedsAvailableCash.value &&
      !commentTooLong.value
  );

  function submit(): void {
    if (!canSubmit.value || parsedAmount.value == null) return;

    const trimmedComment = comment.value.trim();
    modals.dialogSubmit('caja-manual-movement', {
      amount: parsedAmount.value,
      description: trimmedComment !== '' ? trimmedComment : null,
    });
  }
</script>

<template>
  <div class="pos-v2-caja-manual-movement-modal">
    <div class="modal-header">
      <h2 class="modal-title">{{ title }}</h2>
      <span class="modal-subtitle">{{ message }}</span>
    </div>

    <div class="modal-body">
      <div class="field">
        <label class="field-label">{{ $t('cajaV2.amountLabel') }}</label>
        <PosUiInput
          :value="amountValue"
          type="text"
          inputmode="decimal"
          size="lg"
          :prefix="currencySymbol"
          :placeholder="i18nT('cajaV2.amountPlaceholder')"
          @input="(v) => (amountValue = v)"
          @enter="submit"
        />
        <span v-if="amountError" class="field-error">{{ amountError }}</span>
      </div>

      <div class="field">
        <label class="field-label">{{ $t('cajaV2.commentLabel') }}</label>
        <PosUiInput
          :value="comment"
          type="text"
          size="lg"
          :maxlength="MAX_COMMENT_CHARS"
          :placeholder="i18nT('cajaV2.commentPlaceholder')"
          @input="(v) => (comment = v)"
          @enter="submit"
        />
        <span class="field-counter" :class="{ over: commentTooLong }">
          {{ commentLength }}/{{ MAX_COMMENT_CHARS }}
        </span>
      </div>
    </div>

    <div class="modal-footer">
      <PosUiButton
        color="neutral"
        variant="outline"
        size="lg"
        class="btn-cancel"
        @click="modals.dialogSubmit('caja-manual-movement', null)"
      >
        {{ $t('posV2.cancel') }}
      </PosUiButton>
      <PosUiButton
        :color="isInflow ? 'success' : 'error'"
        variant="solid"
        size="lg"
        class="btn-confirm"
        :disabled="!canSubmit"
        @click="submit"
      >
        {{ title }}
      </PosUiButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .pos-v2-caja-manual-movement-modal {
    @apply tw-flex tw-w-full tw-flex-col tw-gap-4 tw-p-4;

    .modal-header {
      @apply tw-flex tw-flex-col tw-gap-0.5;

      .modal-title {
        @apply tw-text-lg tw-font-bold tw-text-neutral-900;
      }

      .modal-subtitle {
        @apply tw-text-sm tw-text-neutral-500;
      }
    }

    .modal-body {
      @apply tw-flex tw-flex-col tw-gap-4;

      .field {
        @apply tw-flex tw-flex-col tw-gap-1;

        .field-label {
          @apply tw-text-sm tw-font-medium tw-text-neutral-700;
        }

        .field-error {
          @apply tw-text-sm tw-text-red-600;
        }

        .field-counter {
          @apply tw-self-end tw-text-xs tw-tabular-nums tw-text-neutral-400;

          &.over {
            @apply tw-font-semibold tw-text-red-600;
          }
        }
      }
    }

    .modal-footer {
      @apply tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-2;

      :deep(.btn-cancel),
      :deep(.btn-confirm) {
        @apply tw-min-w-[130px];
      }
    }
  }
</style>
